import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {AspectRatio, Box, useBreakpointValue} from '@chakra-ui/react'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {isServer} from '../utils/utils'
import {breakpoints} from '../theme/_utils'
import React from 'react'
import {useIsLocalhost} from './use-is-localhost'

export const getDamBaseUrl = () => {
    const {customParams} = getConfig()
    const baseUrl = `${customParams?.dam?.protocol}://${customParams?.dam?.domain}`

    return baseUrl
}

export const getDamProductUrl = () => {
    const {customParams} = getConfig()
    const baseUrl = `${customParams?.dam?.protocol}://${customParams?.dam?.products}`

    return baseUrl
}

export const windowGetBreakpoint = (width) => {
    if (width < parseInt(breakpoints.md)) return 'mobile'
    else if (width < parseInt(breakpoints.lg)) return 'tablet_768'
    else if (width < parseInt(breakpoints.xl)) return 'tablet_1024'
    else return 'desktop'
}

/**
 * Ratio utils
 */

const renditions = [{key: 'mobile'}, {key: 'tablet_768'}, {key: 'tablet_1024'}, {key: 'desktop'}]

export const getAspectRatioValue = (rendition) => {
    const [w, h] = rendition.split('x').map(Number)
    if (isNaN(w) || isNaN(h) || h === 0) return null
    return (w / h).toFixed(3) // ad esempio 1.778 per 16:9
}

const keys = renditions.map((r) => r.key)
const getSingleAspectRatio = (preferredKeys, aspectRatioMap) => {
    for (const key of preferredKeys) {
        if (keys.includes(key)) return Number(aspectRatioMap[key])
    }
    return null
}

export const getRatioObject = (aspectRatioMap) => {
    const ratios = {}
    const base = getSingleAspectRatio(
        ['mobile', 'tablet_768', 'tablet_1024', 'desktop'],
        aspectRatioMap,
    )
    const md = getSingleAspectRatio(['tablet_768', 'tablet_1024', 'desktop'], aspectRatioMap)
    const lg = getSingleAspectRatio(['tablet_1024', 'desktop'], aspectRatioMap)
    const xl = getSingleAspectRatio(['desktop'], aspectRatioMap)

    if (base) ratios.base = base
    if (md) ratios.md = md
    if (lg) ratios.lg = lg
    if (xl) ratios.xl = xl

    return ratios
}
// In useDamImage.js

/**
 * Helper to check if an image object has valid data for rendering.
 * An image is considered valid if it has both a rendition string and a dam_image with an ID.
 * @param {object} image - The image object for a specific breakpoint.
 * @returns {boolean} - True if the image is valid, false otherwise.
 */
const isImageValid = (image) => {
    return image && image.rendition && image.dam_image?.[0]?.id
}
/**
 * Creates a complete image set from a potentially incomplete one.
 * This function ensures every breakpoint has valid image data by using a fallback.
 * It preserves the original rendition for each breakpoint to maintain the correct aspect ratio,
 * and only replaces the dam_image object if it's missing or invalid.
 *
 * @param {object} imageSet - The original imageSet object from the CMS, which may have missing data.
 * @returns {object} A new, complete imageSet object ready for rendering.
 */
export const createCompleteImageSet = (imageSet) => {
    if (!imageSet) {
        return {}
    }

    // 1. Find the first VALID dam_image to use as a fallback.
    const fallbackPriority = ['desktop', 'tablet_1024', 'tablet_768', 'mobile']
    const fallbackImage = fallbackPriority.map((key) => imageSet[key]).find(isImageValid)

    // If no valid image exists at all, return the original to be safe.
    if (!fallbackImage) {
        return imageSet
    }
    const fallbackDamImage = fallbackImage.dam_image

    // 2. Create the new "hybrid" object.
    const completeSet = {}
    const allKeys = ['mobile', 'tablet_768', 'tablet_1024', 'desktop']

    allKeys.forEach((key) => {
        const originalData = imageSet[key]

        // If the breakpoint doesn't exist in the original object, skip it.
        if (!originalData) {
            return
        }

        // KEEP the original rendition. This is crucial.
        const rendition = originalData.rendition

        // USE the original dam_image if it's valid, otherwise use the fallback.
        const dam_image = isImageValid(originalData) ? originalData.dam_image : fallbackDamImage

        // Assemble the new object for this breakpoint.
        completeSet[key] = {rendition, dam_image}
    })

    return completeSet
}
/**
 * Calculates a responsive aspect ratio object for Chakra UI from an imageSet object.
 * This function is less strict than getRatiosAndSOurcesFromImageObj because
 * it only requires the 'rendition' string, not the 'dam_image.id'.
 * It's ideal for calculating the ratio for other media, like videos.
 * @param {object} imageSetObj - The imageSet object, e.g., { mobile: { rendition: '...' } }
 * @returns {object} A responsive ratio object for Chakra, e.g., { base: 1.31, lg: 1.36 }
 */
export const getAspectRatioObjectFromImageSet = (imageSetObj) => {
    // Return an empty object if the input is falsy to prevent errors.
    if (!imageSetObj) {
        return {}
    }

    const aspectRatioMap = {}

    // Iterate through the defined breakpoints (mobile, desktop, etc.).
    breakpointsObject.forEach(({key}) => {
        const rendition = imageSetObj?.[key]?.rendition

        // LESS STRICT CONDITION: We only check for the 'rendition' string.
        if (rendition) {
            aspectRatioMap[key] = getAspectRatioValue(rendition)
        }
    })

    // Use the existing getRatioObject helper to build the final responsive object.
    return getRatioObject(aspectRatioMap)
}

const breakpointsObject = [
    {key: 'mobile', media: '(max-width: 767px)'},
    {key: 'tablet_768', media: '(min-width: 768px) and (max-width: 1023px)'},
    {key: 'tablet_1024', media: '(min-width: 1024px) and (max-width: 1279px)'},
    {key: 'desktop', media: '(min-width: 1280px)'},
]

export const getRatiosAndSOurcesFromImageObj = (contentStackObj) => {
    if (!contentStackObj) return []

    const baseUrl = getDamBaseUrl()

    const aspectRatioMap = {}

    const sources = breakpointsObject
        .map(({key, media}) => {
            const rendition = contentStackObj?.[key]?.rendition
            const damImage = contentStackObj?.[key]?.dam_image?.[0]

            if (!rendition || !damImage?.id) return null

            aspectRatioMap[key] = getAspectRatioValue(rendition)

            const url = `${baseUrl}/${rendition}/assets/${damImage.id}`

            return <source key={key} srcSet={url} media={media} />
        })
        .filter(Boolean)

    const ratio = getRatioObject(aspectRatioMap)
    return [ratio, sources]
}

export const getDamImagePicture = (
    contentStackObj,
    altImg = '',
    isFirstElement = false,
    ratioProp,
    imageStyles = {},
    gradientOverlay = null,
    fillContainer = false,
) => {
    const baseUrl = getDamBaseUrl()
    const imageGroup = createCompleteImageSet(contentStackObj)
    const [ratio, sources] = getRatiosAndSOurcesFromImageObj(imageGroup)
    const ratioToUse = ratioProp || ratio
    const fallback = breakpointsObject.find(({key}) => {
        const rendition = imageGroup?.[key]?.rendition
        const damImage = imageGroup?.[key]?.dam_image?.[0]
        return rendition && damImage?.id
    })

    if (!fallback) return null

    const fallbackUrl = `${baseUrl}/${imageGroup[fallback.key]?.rendition}/assets/${
        imageGroup[fallback.key]?.dam_image[0]?.id
    }`

    if (!fallbackUrl) return null

    const ImageContainer = fillContainer ? Box : AspectRatio

    return (
        <ImageContainer
            data-id="aspect-ratio-dam-image"
            {...(fillContainer ? {height: '100%'} : {ratio: ratioToUse})}
            width="100%"
            position="relative"
        >
            <Box width="100%" height="100%" position="relative">
                <picture
                    style={{
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {sources}
                    <img
                        src={fallbackUrl}
                        alt={altImg || ''}
                        style={{
                            width: '100%',
                            height: '100%',
                            ...imageStyles,
                        }}
                        // eslint-disable-next-line react/no-unknown-property
                        fetchpriority={isFirstElement ? 'high' : 'low'}
                    />
                </picture>
                {gradientOverlay && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        width="100%"
                        height="100%"
                        zIndex={1}
                        bgImage={gradientOverlay}
                        pointerEvents="none"
                    />
                )}
            </Box>
        </ImageContainer>
    )
}

/**
 *
 * @param {object} contentStackObj - content stack field that contains the image obj
 * @returns {string} - full path to the image
 *
 */
const useDamImage = (contentStackObj) => {
    const {res} = useServerContext()

    let breakpoint = useBreakpointValue(
        {
            base: 'mobile',
            md: 'tablet_768',
            lg: 'tablet_1024',
            xl: 'desktop',
        },
        {
            fallback: null,
        },
    )

    if (!breakpoint) {
        if (res?.locals?.ua?.isMobile) breakpoint = 'mobile'
        else if (res?.locals?.ua?.isTablet) breakpoint = 'tablet_1024'
        else if (res?.locals?.ua?.isDesktop) breakpoint = 'desktop'
        else if (isServer) breakpoint = 'mobile'
        else {
            breakpoint = windowGetBreakpoint(window?.innerWidth)
        }
    }

    const DEFAULT_RENDITION = 16 / 9
    const EMPTY_DAM_IMAGE = {
        url: '',
        ratio: DEFAULT_RENDITION,
    }

    if (!contentStackObj || !/x/.test(contentStackObj?.[breakpoint]?.rendition))
        return EMPTY_DAM_IMAGE

    let damImage = contentStackObj?.[breakpoint]?.dam_image?.[0]

    if (!damImage && breakpoint === 'tablet_768') {
        damImage = contentStackObj?.['mobile']?.dam_image?.[0]
    }

    if (!damImage && breakpoint === 'tablet_1024') {
        damImage = contentStackObj?.['desktop']?.dam_image?.[0]
    }

    const baseUrl = getDamBaseUrl()
    const ratioFactors = contentStackObj[breakpoint].rendition.split('x')

    return {
        url: damImage?.id
            ? `${baseUrl}/${contentStackObj[breakpoint].rendition}/assets/${damImage?.id}`
            : '',
        ratio: ratioFactors[1] ? ratioFactors[0] / ratioFactors[1] : DEFAULT_RENDITION,
    }
}

export default useDamImage

/**
 * Returns the base URL to prepend to video paths.
 * On localhost, videos are served from the production domain since they are not available locally.
 * On production/staging, paths are relative so no base URL is needed.
 */
export const useEditorialVideoBaseUrl = () => {
    const isLocalhost = useIsLocalhost()
    return isLocalhost ? 'https://www.delonghi.com' : ''
}
